import React, { useState, useEffect } from "react";
import Modal from "react-modal/lib/components/Modal";
import HeaderSimple from "../../../../components/header/simple";
import { BtnBack } from "../../../../components/button/button-back";
import Loading from "../../../../components/loading";
import reqComplementos from "../../../../services/reqMultiTenant/reqComplementos";
import Notification from "../../../../components/toast";
import { Plus, Trash2, Search, ListChecks, RotateCcw } from "lucide-react";
import ProductSearch from "../../../../components/product-search";

function ModalBindProducts({ isOpen, handleClose, idGrupoSelecionado, grupoDescricao }) {
	const [isLoadingInitial, setIsLoadingInitial] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	// Controle de Abas
	const [activeTab, setActiveTab] = useState("selected"); // 'search' | 'selected'

	// Lista de produtos atualmente vinculados (o cache em memória antes de salvar)
	const [vinculosOriginais, setVinculosOriginais] = useState([]); // Array Pristine que veio do Back
	const [vinculosAtivos, setVinculosAtivos] = useState([]); // Array mutável na tela

	useEffect(() => {
		const fetchVinculosIniciais = async () => {
			setIsLoadingInitial(true);
			try {
				// Traz apenas os produtos que o INNER JOIN apontou como atrelados
				const res = await reqComplementos.listarProdutosVinculo(idGrupoSelecionado);
				const data = res.data || [];
				setVinculosOriginais(data.map(v => v.id_produto)); 
				setVinculosAtivos(data);
			} catch (error) {
				console.error("Erro ao buscar vínculos iniciais", error);
				Notification.showToast({
					message: "Falha ao carregar produtos vinculados.",
					type: "error",
				});
				handleClose();
			} finally {
				setIsLoadingInitial(false);
			}
		};

		if (isOpen && idGrupoSelecionado) {
			setHasUnsavedChanges(false);
			setActiveTab("selected");
			fetchVinculosIniciais();
		}
	}, [isOpen, idGrupoSelecionado, handleClose]);

	// Prevenção de fechamento
	const handleSafeClose = () => {
		if (hasUnsavedChanges) {
			const leave = window.confirm("Existem vínculos alterados! Deseja realmente sair sem salvar?");
			if (!leave) return;
		}
		handleClose();
	};

	// Função que recebe o Produto clicado lá de dentro do `<ProductSearch />`
	const handleAddVinculo = (prod) => {
		const prodId = prod.id_produto || prod.id;
		const jaExiste = vinculosAtivos.find((v) => v.id_produto === prodId);
		if (jaExiste) {
			if (jaExiste.marked_for_deletion) {
				setVinculosAtivos((prev) => 
					prev.map(v => v.id_produto === prodId ? { ...v, marked_for_deletion: false } : v)
				);
				setHasUnsavedChanges(true);
				Notification.showToast({ message: "Produto restaurado à lista de vínculos.", type: "success" });
			} else {
				Notification.showToast({ message: "Produto já está na lista!", type: "info" });
			}
			return;
		}

		setVinculosAtivos((prev) => [...prev, { id_produto: prodId, nome_produto: prod.nome, origin: "new" }]);
		setHasUnsavedChanges(true);
		Notification.showToast({
			message: "Produto adicionado à lista de vínculos. Não se esqueça de salvar!",
			type: "success",
		});
	};

	// Remove da lista local (Soft Delete para itens que vieram do banco, Hard Delete para novos)
	const handleRemoveVinculo = (idProd) => {
		const prod = vinculosAtivos.find(v => v.id_produto === idProd);
		if (prod && prod.origin === 'new') {
			setVinculosAtivos((prev) => prev.filter((v) => v.id_produto !== idProd));
		} else {
			setVinculosAtivos((prev) => 
				prev.map(v => v.id_produto === idProd ? { ...v, marked_for_deletion: true } : v)
			);
		}
		setHasUnsavedChanges(true);
	};

	// Grava de volta no servidor
	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSaving(true);
		try {
			const arrayDML = [];
			// Apenas os itens que estão VISÍVEIS e ATIVOS na lista, excluindo os que têm 'marked_for_deletion'
			const arrayIdsAtivos = vinculosAtivos
				.filter(v => !v.marked_for_deletion)
				.map(v => v.id_produto);

			// 1. Quem foi Adicionado? (Está nos ativos, mas NÃO estava nos originais)
			arrayIdsAtivos.forEach(idAtual => {
				if (!vinculosOriginais.includes(idAtual)) {
					arrayDML.push({
						id_complementos_grupo: idGrupoSelecionado,
						id_produto: idAtual,
						acao: 'A' // ADD
					});
				}
			});

			// 2. Quem foi Removido? (Estava nos originais, mas NÃO está mais nos ativos)
			vinculosOriginais.forEach(idOriginal => {
				if (!arrayIdsAtivos.includes(idOriginal)) {
					arrayDML.push({
						id_complementos_grupo: idGrupoSelecionado,
						id_produto: idOriginal,
						acao: 'D' // DELETE
					});
				}
			});

			if (arrayDML.length === 0) {
				handleClose(); // Sai silenciosamente se clicou em salvar sem mudar nada
				return;
			}

			await reqComplementos.syncComplementosProdutos(idGrupoSelecionado, arrayDML);

			Notification.showToast({
				message: "Vínculos salvos com sucesso!",
				type: "success",
			});
			handleClose();
		} catch (error) {
			console.error(error);
			const msg = error.response?.data?.message || error.message || "Erro desconhecido";
			Notification.showToast({
				message: "Erro ao salvar vínculos: " + msg,
				type: "error",
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleSafeClose}
			overlayClassName="react-modal-overlay"
			className="modal-content-form modal-product-resize"
			shouldCloseOnOverlayClick={false}
		>
			<div
				className="flex flex-col h-full bg-white rounded shadow-xl overflow-hidden"
				style={{ maxHeight: "90vh" }}
			>
				<HeaderSimple
					title="Vincular Produtos ao Grupo"
					subTitle={`Grupo Atual: ${grupoDescricao || ""}`}
					handleCloseModalRegister={handleSafeClose}
				/>

				{/* Navbar de Abas */}
				<div className="flex border-b bg-gray-50/50 px-4 mt-2 shrink-0">
					<button
						onClick={() => setActiveTab("selected")}
						className={`py-3 px-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
							activeTab === "selected"
								? "border-blue-600 text-blue-600 bg-blue-50/30"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						}`}
					>
						<ListChecks size={16} />
						Produtos Selecionados
						<span
							className={`ml-2 px-2 py-0.5 rounded-full text-xs text-white ${activeTab === "selected" ? "bg-blue-600" : "bg-gray-400"}`}
						>
							{vinculosAtivos.length}
						</span>
						{hasUnsavedChanges && (
							<span className="ml-1 w-2 h-2 bg-red-500 rounded-full" title="Alterações não salvas"></span>
						)}
					</button>

					<button
						onClick={() => setActiveTab("search")}
						className={`py-3 px-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
							activeTab === "search"
								? "border-blue-600 text-blue-600 bg-blue-50/30"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						}`}
					>
						<Search size={16} />
						Pesquisar Novos Produtos
					</button>
				</div>

				{isLoadingInitial ? (
					<div className="flex flex-1 justify-center py-10 scale-125">
						<Loading mensagem="Buscando vínculos atuais no sistema..." />
					</div>
				) : (
					<form onSubmit={handleSubmit} className="form flex-1 flex flex-col overflow-hidden h-full">
						<main className="flex-1 flex flex-col overflow-hidden min-h-0 p-4 bg-gray-100">
							{/* ABA 1: PESQUISA */}
							{activeTab === "search" && (
								<div className="flex-1 flex flex-col min-h-0 animate-fade-in">
									<div className="flex-1 flex flex-col shadow-sm rounded-md border border-gray-300 bg-white overflow-hidden min-h-0">
										<ProductSearch
											key={isOpen ? "open" : "closed"}
											onSelectProduct={handleAddVinculo}
											onRemoveProduct={handleRemoveVinculo}
											customActionTitle="Adicionar ao Vínculo"
											customActionIcon={<Plus size={16} />}
											linkedIds={vinculosAtivos.filter(v => !v.marked_for_deletion).map((v) => v.id_produto)}
										/>
									</div>
								</div>
							)}

							{/* ABA 2: LISTA DE ESCOLHIDOS */}
							{activeTab === "selected" && (
								<div className="flex-1 flex flex-col min-h-0 border rounded-md shadow-sm border-gray-300 bg-white animate-fade-in overflow-hidden">
									<div className="flex-1 overflow-y-auto w-full">
										<table className="table table-hover mb-0 w-full table-fixed">
											<thead className="color-table sticky top-0 z-10 shadow-sm border-b">
												<tr>
													<th className="text-left font-semibold py-2 px-4 shadow-sm">
														<div className="flex items-center gap-2">
															<span className="text-color-table">Produto Vinculado ({vinculosAtivos.filter(v => !v.marked_for_deletion).length})</span>
															{hasUnsavedChanges && (
																<span className="text-[10px] text-orange-300 font-normal italic bg-black/20 px-2 py-0.5 rounded-full">
																	* Pendências para salvar
																</span>
															)}
														</div>
													</th>
													<th className="text-right font-semibold py-2 px-4 w-20 shadow-sm"><span className="text-color-table">Ação</span></th>
												</tr>
											</thead>
											<tbody>
												{vinculosAtivos.length > 0 ? (
													vinculosAtivos.map((prod) => (
														<tr
															key={prod.id_produto}
															className={`group border-b last:border-0 ${prod.marked_for_deletion ? 'bg-gray-50 opacity-60' : 'hover:bg-red-50'}`}
														>
															<td className={`text-left font-medium align-middle py-3 px-4 ${prod.marked_for_deletion ? 'line-through text-gray-400' : 'text-gray-800'}`}>
																{prod.nome_produto}
																{prod.origin === "new" && !prod.marked_for_deletion && (
																	<span className="ml-2 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full font-semibold">
																		Novo Adicionado
																	</span>
																)}
																{prod.marked_for_deletion && (
																	<span className="ml-2 text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full font-semibold">
																		Removido
																	</span>
																)}
															</td>
															<td className="text-right align-middle py-3 px-4 w-20">
																{prod.marked_for_deletion ? (
																	<button
																		type="button"
																		title="Desfazer remoção"
																		className="text-gray-500 hover:text-blue-600 transition-colors p-2 rounded-full hover:bg-blue-100"
																		onClick={() => {
																			setVinculosAtivos((prev) => 
																				prev.map(v => v.id_produto === prod.id_produto ? { ...v, marked_for_deletion: false } : v)
																			);
																			setHasUnsavedChanges(true);
																		}}
																	>
																		<RotateCcw size={18} />
																	</button>
																) : (
																	<button
																		type="button"
																		title="Remover produto da lista"
																		className="text-gray-400 hover:text-red-600 transition-colors p-2 rounded-full hover:bg-red-100"
																		onClick={() => handleRemoveVinculo(prod.id_produto)}
																	>
																		<Trash2 size={18} />
																	</button>
																)}
															</td>
														</tr>
													))
												) : (
													<tr>
														<td
															colSpan="2"
															className="text-center text-gray-500 py-12 px-4 shadow-inner bg-gray-50 border-t"
														>
															<div className="flex flex-col items-center gap-3">
																<ListChecks size={40} className="text-gray-300" />
																<span className="text-base text-gray-600">
																	Nenhum vínculo marcado neste grupo.
																</span>
																<span className="text-sm">
																	Vá até a aba{" "}
																	<strong
																		className="cursor-pointer text-blue-600 underline"
																		onClick={() => setActiveTab("search")}
																	>
																		Pesquisar Novos Produtos
																	</strong>{" "}
																	e comece a vincular as opções.
																</span>
															</div>
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								</div>
							)}
						</main>

						<footer className="mt-auto border-t pt-3 pb-2 px-4 bg-gray-50/80 shrink-0 flex justify-between items-center z-20">
							<BtnBack onClick={handleSafeClose} isSubmitting={isSaving} />
							<div className="flex gap-2 ml-auto">
								<button
									type="submit"
									className="btn btn-success font-medium px-4 py-2 flex items-center justify-center gap-2"
									disabled={isSaving}
								>
									{isSaving ? (
										<>
											<span
												className="spinner-border spinner-border-sm"
												role="status"
												aria-hidden="true"
											></span>
											Sincronizando...
										</>
									) : (
										"Salvar Vínculos"
									)}
								</button>
							</div>
						</footer>
					</form>
				)}
			</div>
		</Modal>
	);
}

export default ModalBindProducts;
